import api from './api';
import { 
  AutomationRule, 
  TaskAutomationRule, 
  TaskCategory, 
  AutomationExecution,
  AutomationStats,
  DropdownData,
  TaskAutomationFormData,
  AutomationRuleFormData,
  TaskCategoryFormData,
  Pagination,
  AutomationApproval
} from '../types/Automation';

// AUTOMATION RULES API

export const getAutomationRules = async (params?: {
  page?: number;
  limit?: number;
  search?: string;
  ruleType?: string;
  isActive?: boolean;
  sortBy?: string;
  sortOrder?: string;
}): Promise<{ rules: AutomationRule[]; pagination: Pagination }> => {
  const response = await api.get('/automation/rules', { params });
  return {
    rules: response.data.data || [],
    pagination: response.data.pagination || {
      currentPage: 1,
      totalPages: 1,
      totalRecords: 0,
      hasNextPage: false,
      hasPrevPage: false
    }
  };
};

export const getAutomationRule = async (id: number): Promise<AutomationRule & { taskAutomationRules: TaskAutomationRule[] }> => {
  const response = await api.get(`/automation/rules/${id}`);
  return response.data.data;
};

export const createAutomationRule = async (data: AutomationRuleFormData): Promise<AutomationRule> => {
  const response = await api.post('/automation/rules', data);
  return response.data.data;
};

export const updateAutomationRule = async (id: number, data: Partial<AutomationRuleFormData>): Promise<AutomationRule> => {
  const response = await api.put(`/automation/rules/${id}`, data);
  return response.data.data;
};

export const toggleAutomationRule = async (id: number): Promise<AutomationRule> => {
  const response = await api.patch(`/automation/rules/${id}/toggle`);
  return response.data.data;
};

export const deleteAutomationRule = async (id: number): Promise<void> => {
  await api.delete(`/automation/rules/${id}`);
};

// TASK AUTOMATION RULES API

export const getTaskAutomationRules = async (params?: {
  page?: number;
  limit?: number;
  search?: string;
  stageId?: number;
  assigneeId?: number;
  isActive?: boolean;
  sortBy?: string;
  sortOrder?: string;
}): Promise<{ rules: TaskAutomationRule[]; pagination: Pagination }> => {
  try {
    const response = await api.get('/automation/task-rules', { params });
    console.log('API Response:', response.data);
    return {
      rules: response.data.data || [],
      pagination: response.data.pagination || {
        currentPage: 1,
        totalPages: 1,
        totalRecords: 0,
        hasNextPage: false,
        hasPrevPage: false
      }
    };
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
};

export const getTaskAutomationRule = async (id: number): Promise<TaskAutomationRule> => {
  const response = await api.get(`/automation/task-rules/${id}`);
  return response.data.data;
};

export const createTaskAutomationRule = async (data: TaskAutomationFormData): Promise<TaskAutomationRule> => {
  const response = await api.post('/automation/task-rules', data);
  return response.data.data;
};

export const updateTaskAutomationRule = async (id: number, data: Partial<TaskAutomationFormData>): Promise<TaskAutomationRule> => {
  const response = await api.put(`/automation/task-rules/${id}`, data);
  return response.data.data;
};

export const deleteTaskAutomationRule = async (id: number): Promise<void> => {
  await api.delete(`/automation/task-rules/${id}`);
};

// TASK CATEGORIES API

export const getTaskCategories = async (params?: {
  page?: number;
  limit?: number;
  search?: string;
  isActive?: boolean;
  sortBy?: string;
  sortOrder?: string;
}): Promise<{ categories: TaskCategory[]; pagination: Pagination }> => {
  const response = await api.get('/automation/categories', { params });
  return response.data;
};

export const getActiveTaskCategories = async (): Promise<TaskCategory[]> => {
  const response = await api.get('/automation/categories/active');
  return response.data.data;
};

export const createTaskCategory = async (data: TaskCategoryFormData): Promise<TaskCategory> => {
  const response = await api.post('/automation/categories', data);
  return response.data.data;
};

export const updateTaskCategory = async (id: number, data: Partial<TaskCategoryFormData>): Promise<TaskCategory> => {
  const response = await api.put(`/automation/categories/${id}`, data);
  return response.data.data;
};

export const deleteTaskCategory = async (id: number): Promise<void> => {
  await api.delete(`/automation/categories/${id}`);
};

// AUTOMATION EXECUTIONS API

export interface ExecutionFilters {
  page?: number;
  limit?: number;
  ruleId?: number;
  leadId?: number;
  status?: string;
  executionType?: string;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

export const getAutomationExecutions = async (params?: ExecutionFilters): Promise<{ executions: AutomationExecution[]; pagination: Pagination }> => {
  const response = await api.get('/automation/executions', { params });
  return {
    executions: response.data.data || [],
    pagination: response.data.pagination || {
      currentPage: 1,
      totalPages: 1,
      totalRecords: 0,
      hasNextPage: false,
      hasPrevPage: false,
      limit: 20
    }
  };
};

export const getExecutionTypes = async (): Promise<string[]> => {
  const response = await api.get('/automation/executions/types');
  return response.data.data || [];
};

export const getExecutionRules = async (): Promise<{ id: number; name: string }[]> => {
  const response = await api.get('/automation/executions/rules');
  return response.data.data || [];
};

// DROPDOWN DATA API

export const getDropdownData = async (): Promise<DropdownData> => {
  const response = await api.get('/automation/dropdown-data');
  return response.data.data;
};

// AUTOMATION STATS API

export const getAutomationStats = async (params?: {
  startDate?: string;
  endDate?: string;
}): Promise<AutomationStats> => {
  const response = await api.get('/automation/stats', { params });
  return response.data.data;
};

// TEST AUTOMATION RULE API

export const testAutomationRule = async (ruleData: TaskAutomationFormData, testLeadData: {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
}): Promise<any> => {
  const response = await api.post('/automation/test-rule', {
    ruleData,
    testLeadData
  });
  return response.data.data;
};

// AUTOMATION APPROVAL API

export const getAutomationApprovals = async (params?: {
  page?: number;
  limit?: number;
  status?: 'pending' | 'approved' | 'rejected';
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}): Promise<{ approvals: AutomationApproval[]; pagination: Pagination }> => {
  const response = await api.get('/automation/approvals', { params });
  return {
    approvals: response.data.data.approvals || [],
    pagination: response.data.data.pagination || {
      currentPage: 1,
      totalPages: 1,
      totalRecords: 0,
      hasNextPage: false,
      hasPrevPage: false
    }
  };
};

export const getAutomationApproval = async (id: number): Promise<AutomationApproval> => {
  const response = await api.get(`/automation/approvals/${id}`);
  return response.data.data;
};

export const approveAutomation = async (id: number): Promise<AutomationApproval> => {
  const response = await api.post(`/automation/approvals/${id}/approve`);
  return response.data.data;
};

export const rejectAutomation = async (id: number): Promise<AutomationApproval> => {
  const response = await api.post(`/automation/approvals/${id}/reject`);
  return response.data.data;
};
